import { BodyprintMode } from "@/enums";
import Vue from "vue";
import { VectorToFrom } from "@/models/vector";
import { Plane } from "@/models/plane";
import { mapGetters } from "vuex";

export default {
  namespaced: true,
  state: {
    loading: true,
    mode: BodyprintMode.NONE,
    zoom: {},
    queueItem: {},
    queueEmpty: false,
    magnifier: true,
    imageSide: null,
    imageBack: null,
    blurDataBack: null,
    blurDataSide: null,
    imageIdBack: null,
    imageIdSide: null,
    measureImage: {},
    measureStep: 0,
    growthFactor: "",
    modifiedPoints: [],
    activeCoordinates: {},
    activePoints: {},
    newCoordinates: {},
    windowHeight: window.innerHeight - 120,
    imageHeight: 1000,
    imageWidth: 750,
    queueSize: 0,
    isQueueItem: false,
    queue: {},
    currentStep: "review",
    bodyprintSizes: [],
    bodyprints: [],
    declinedBodyprints: [],
    bodyprint: null,
    bodyprintInfo: null,
    usedControls: false,
    usersInQueue: [],
    itemsForUser: [],
    itemsForQueueItem: [],
    usersInBlurQueue: [],
    itemsForBlurUser: [],
    itemsForBlurQueueItem: [],
    // Blur related items
    blurMaxBoxWidth: 300, // Maximum blur box width in original image pixels
    blurMaxBoxHeight: 300, // Maximum blur box height in original image pixels
    blurMaxPositionRatio: 0.5, // Box must stay within top 25% of image
    blurSimpleResizeMargin: 12.5 // Max resize margin for simple mode (percentage of original box size)
  },
  computed: {
    ...mapGetters({
      getPointsByArea: "configuration/getPointsByArea",
      getGuide: "bodyprint/getGuide",
      getArea: "configuration/getArea",
      getAreas: "configuration/getAreas",
      getPoint: "configuration/getPoint",
      getNewCoordinates: "bodyprint/getNewCoordinates"
    })
  },
  getters: {
    isQueueItem: state => {
      return state.isQueueItem;
    },
    getMagnifier: state => {
      return state.magnifier;
    },
    getImage: state => projection => {
      if (projection === "left") {
        return state.imageSide;
      } else {
        return state.imageBack;
      }
    },
    getMeasureImage: state => {
      return state.measureImage;
    },
    // getActivePoints: (state, getters) => {
    //   this.getArea.handles[i]
    //   return state.activePoints;
    // },
    getBodyprintSizes: state => {
      return state.bodyprintSizes;
    },
    getBodyPrint: state => {
      return state.bodyprint;
    },
    getBodyPrintInfo: state => {
      return state.bodyprintInfo;
    },
    getBodyPrints: state => {
      return state.bodyprints;
    },
    getDeclinedBodyprints: state => {
      return state.declinedBodyprints;
    },
    getGrowthFactor: state => {
      return state.windowHeight / state.imageHeight;
    },
    // getSideGuides: state => {
    //   return state.bodyprint.guides.find(guide => guide.projection === "left");
    // },
    getSideWidth: (state, getters) => {
      return (
        getters.getSideGuides.crop.width *
        state.imageWidth *
        state.getGrowthFactor
      );
    },
    getBackWidth: (state, getters) => {
      return (
        getters.getBackGuides.crop.width *
        state.imageWidth *
        state.getGrowthFactor
      );
    },
    getPictureScale: state => {
      return state.windowHeight / state.imageHeight;
    },
    getPictureWidth: (state, getters) => {
      return state.imageWidth * getters.getPictureScale;
    },
    getPictureHeight: state => {
      return state.windowHeight;
    },
    getBackGuides: state => {
      return state.bodyprint.guides.find(guide => guide.projection === "back");
    },
    getCurrentStep: state => {
      return state.currentStep;
    },
    getSelectedMeasurePoint: state => {
      return state.selectedMeasurePoint;
    },
    getUsedControls: state => {
      return state.usedControls;
    },
    getNewCoordinates: state => {
      return state.activeCoordinates;
    },
    getCropBack: state => {
      return state.bodyprint.guides.find(guide => guide.projection === "back");
    },
    getCropSide: state => {
      return state.bodyprint.guides.find(guide => guide.projection === "left");
    },
    getGuide: state => projection => {
      return state.bodyprint.guides.find(
        guide => guide.projection === projection
      );
    },
    getGrowthFactorCrop: state => {
      return state.windowHeight / (state.height * state.windowHeight);
    },
    getMeasureStep: state => {
      return state.measureStep;
    },
    getModifiedPoints: state => {
      return state.modifiedPoints;
    },
    isPointModified: state => name => {
      return state.modifiedPoints.includes(name);
    },
    getQueueItem: state => {
      return state.queueItem;
    },
    getQueueEmpty: state => {
      return state.queueEmpty;
    },
    getBodyprintMode: state => {
      return state.mode;
    },
    getImageSide: state => {
      return state.imageSide;
    },
    getImageBack: state => {
      return state.imageBack;
    },
    getBlurDataBack: state => {
      return state.blurDataBack;
    },
    getBlurDataSide: state => {
      return state.blurDataSide;
    },
    getImageIdBack: state => {
      return state.imageIdBack;
    },
    getImageIdSide: state => {
      return state.imageIdSide;
    },
    getOriginalImageWidth: state => {
      return state.imageWidth;
    },
    getOriginalImageHeight: state => {
      return state.imageHeight;
    },
    // Blur
    getBlurMaxBoxWidth: state => {
      return state.blurMaxBoxWidth;
    },
    getBlurMaxBoxHeight: state => {
      return state.blurMaxBoxHeight;
    },
    getBlurPositionRatio: state => {
      return state.blurMaxPositionRatio;
    },
    getBlurSimpleResizeMargin: state => {
      return state.blurSimpleResizeMargin;
    },
    // List v2
    getUsersInQueue: state => {
      return state.usersInQueue;
    },
    getItemsForUser: state => {
      return state.itemsForUser;
    },
    getItemsForQueueItem: state => {
      return state.itemsForQueueItem;
    },
    // Blur queue
    getUsersInBlurQueue: state => {
      return state.usersInBlurQueue;
    },
    getItemsForBlurUser: state => {
      return state.itemsForBlurUser;
    },
    getItemsForBlurQueueItem: state => {
      return state.itemsForBlurQueueItem;
    }
  },
  mutations: {
    setQueueEmpty(state, payload) {
      state.queueEmpty = payload;
    },
    setQueueItem(state, payload) {
      state.isQueueItem = true;
      state.queueItem = payload;
    },
    setWindowHeight(state, payload) {
      // change 84 to desired
      let newHeight = payload.height - 84;
      let newScale = newHeight / state.imageHeight;
      let newWidth = state.imageWidth * newScale;
      if (newWidth < payload.width / 3) {
        state.windowHeight = newHeight;
      }
    },
    setZoom(state, payload) {
      state.zoom = payload;
    },
    // setModifiedPoint(state, payload) {
    //   console.log('modified point');
    //   console.log(state);
    //   console.log(payload);
    // },
    setMeasureImage(state, payload) {
      state.measureImage = payload;
    },
    SET_IMAGE_WIDTH(state, width) {
      state.imageWidth = width;
    },
    SET_IMAGE_HEIGHT(state, height) {
      state.imageHeight = height;
    },
    setCrop(state, payload) {
      payload[0].width = payload[0].x2 - payload[0].x;
      payload[0].height = payload[0].y2 - payload[0].y;
      payload[0].growthFactor =
        state.windowHeight / (payload[0].height * state.windowHeight);
      let guide = state.bodyprint.guides.find(
        guide => guide.projection === payload[1]
      );
      guide.crop = payload[0];
    },
    setCurrentStep(state, payload) {
      state.currentStep = payload;
    },
    updateConstraints(state, payload) {
      const point_0_index = state.bodyprint.points.findIndex(
        point => point.name === "point_0"
      );

      const point_38_index = state.bodyprint.points.findIndex(
        point => point.name === "point_38"
      );
      if (point_0_index !== -1) {
        const point_0 = state.bodyprint.points[point_0_index];

        if (point_0.y !== 0.0) {
          const guideIndex = state.bodyprint.guides.findIndex(
            guide => guide.projection === payload.projection
          );
          const guide = state.bodyprint.guides[guideIndex];
          const mutation = point_0.y * guide.crop.height;

          guide.crop.y = guide.crop.y + mutation;
          guide.crop.height = guide.crop.height - mutation;

          guide.crop.growthFactor =
            state.windowHeight / (guide.crop.height * state.windowHeight);
          Vue.set(state.bodyprint.guides, guideIndex, guide);

          point_0.y = 0.0;
          Vue.set(state.bodyprint.points, point_0_index, point_0);
        }
      }

      if (point_38_index !== -1) {
        const point_38 = state.bodyprint.points[point_38_index];
        if (point_38.y !== 1.0) {
          const guideIndex = state.bodyprint.guides.findIndex(
            guide => guide.projection === payload.projection
          );
          const guide = state.bodyprint.guides[guideIndex];
          const mutation = (1 - point_38.y) * guide.crop.height;

          guide.crop.height = guide.crop.height - mutation;
          guide.crop.growthFactor =
            state.windowHeight / (guide.crop.height * state.windowHeight);

          Vue.set(state.bodyprint.guides, guideIndex, guide);

          point_38.y = 1.0;
          Vue.set(state.bodyprint.points, point_38_index, point_38);
        }
      }
    },
    setPoint(state, payload) {
      const newPoint = {
        name: payload.name,
        x: payload.x,
        y: payload.y,
        z: payload.z
      };
      const index = state.bodyprint.points.findIndex(
        point => point.name === payload.name
      );
      if (index !== -1) {
        Vue.set(state.bodyprint.points, index, newPoint);
        return;
      }
      return state.bodyprint.points.push(newPoint);
    },
    setModifiedPoint(state, name) {
      if (!state.modifiedPoints.includes(name)) {
        state.modifiedPoints.push(name);
      }
    },
    resetModifiedPoints(state) {
      state.modifiedPoints = [];
    },
    setActivePoints(state, payload) {
      state.activePoints = payload;
    },
    deactivatePoints(state) {
      state.activeCoordinates = {};
    },
    setActiveCoordinates(state, payload) {
      state.activeCoordinates = payload;
    },
    setUsedControls(state, payload) {
      state.usedControls = payload;
    },
    setNewMeasureCoordinates(state, payload) {
      var point = state.activeCoordinates;
      if (payload === "top" || payload === "bottom") {
        if (point.canMoveY) {
          point.y = payload === "top" ? point.y - 1 : point.y + 1;
        }
      }
      if (payload === "left" || payload === "right") {
        if (point.canMoveX) {
          point.x = payload === "left" ? point.x - 1 : point.x + 1;
        }
      }
      state.activeCoordinates = point;
      //watcher..
      state.newCoordinates = point;
      state.usedControls = true;

      return point;
    },
    setNewCropCoordinates(state, payload) {
      var position = state.activeCoordinates;
      if (payload === "top" || payload === "bottom") {
        position.y = payload === "top" ? position.y - 1 : position.y + 1;
      }
      if (payload === "left" || payload === "right") {
        position.x = payload === "left" ? position.x - 1 : position.x + 1;
      }
      state.activeCoordinates = position; //update new coordinates to selected item.
      state.newCoordinates = position; //update new coordinates for triggering watcher.
      state.usedControls = true;
    },
    setMeasureStep(state, payload) {
      payload.step >= payload.areas.length ? (payload.step = 0) : false;
      payload.step < 0 ? (payload.step = payload.areas.length - 1) : false;
      state.measureStep = payload.step;
    },
    setImages(state, images) {
      var backguide = state.bodyprint.guides.find(x => x.projection === "back");
      var leftguide = state.bodyprint.guides.find(x => x.projection === "left");
      var backImage = images.find(x => x._id === backguide.image_id);
      var sideImage = images.find(x => x._id === leftguide.image_id);
      state.imageBack = backImage.data;
      state.imageSide = sideImage.data;
      state.imageIdBack = backImage._id;
      state.imageIdSide = sideImage._id;
      state.blurDataBack = backImage.blur_data || null;
      state.blurDataSide = sideImage.blur_data || null;
    },
    setBodyprintSizes(state, sizes) {
      state.bodyprintSizes = sizes;
    },
    setBodyprint(state, bodyprint) {
      state.bodyprint = bodyprint;
    },
    setBodyprints(state, bodyprints) {
      state.bodyprints = bodyprints;
    },
    setDeclinedBodyprints(state, bodyprints) {
      state.declinedBodyprints = bodyprints;
    },
    setBodyprintInfo(state, bodyprintInfo) {
      state.bodyprintInfo = bodyprintInfo;
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setMode(state, mode) {
      state.mode = mode;
    },
    // List v2
    setUsersInQueue(state, users) {
      state.usersInQueue = users;
    },
    setItemsForUser(state, items) {
      state.itemsForUser = items;
    },
    setItemsForQueueItem(state, items) {
      state.itemsForQueueItem = items;
    },
    // Blur queue
    setUsersInBlurQueue(state, users) {
      state.usersInBlurQueue = users;
    },
    setItemsForBlurUser(state, items) {
      state.itemsForBlurUser = items;
    },
    setItemsForBlurQueueItem(state, items) {
      state.itemsForBlurQueueItem = items;
    },
    setBlurSimpleResizeMargin(state, value) {
      state.blurSimpleResizeMargin = value;
    }
  },
  actions: {
    async setBodyprint({ commit }, bodyprint) {
      //logic for set to normal
      commit("setBodyprint", bodyprint);
    },
    setQueueItemStatus({ getters, rootGetters }, payload) {
      var id = getters.getQueueItem._id;
      return rootGetters["network/getClient"]
        .put(`bodyprint_queue/${id}`, payload, {
          headers: {
            // Overwrite Axios's automatically set Content-Type
            "Content-Type": "application/json"
          }
        })
        .then(() => {
          return true;
        });
    },
    cancelQueueItem({ getters, rootGetters }) {
      var id = getters.getQueueItem._id;
      return rootGetters["network/getClient"]
        .get(`bodyprint_queue/${id}/cancel`)
        .then(() => {
          return true;
        });
    },
    checkBodyprint({ dispatch, commit }) {
      commit("setMode", BodyprintMode.VIEW);
      return dispatch("calculateBodyprint");
    },
    editBodyprint({ dispatch, commit }) {
      commit("setMode", BodyprintMode.EDIT);
      commit("resetModifiedPoints");
      return dispatch("calculateBodyprint");
    },
    calculateBodyprint({ commit, state, getters }) {
      return new Promise(resolve => {
        const bodyprint = JSON.parse(JSON.stringify(state.bodyprint));
        var img = new Image();
        img.src = state.imageSide;

        var imgBack = new Image();
        var imgSide = new Image();

        imgBack.src = state.imageBack;
        imgSide.src = state.imageSide;

        setTimeout(() => {
          commit("SET_IMAGE_WIDTH", img.width);
          commit("SET_IMAGE_HEIGHT", img.height);

          // Use the deep copy for crop heights to avoid reading state that may
          // have been mutated by updateConstraints before this timeout fires.
          let growthFactorSide =
            imgSide.height /
            (bodyprint.guides.find(x => x.projection === "left").crop.height *
              imgSide.height);

          let growthFactorBack =
            imgBack.height /
            (bodyprint.guides.find(x => x.projection === "back").crop.height *
              imgBack.height);

          let cropWidthSide = bodyprint.guides[0].crop.width;
          let cropWidthBack = bodyprint.guides[1].crop.width;
          var pointsProcessed = 0;
          if (bodyprint.points.length === 0) {
            if (cropWidthSide) {
              bodyprint.guides[0].crop.x2 =
                bodyprint.guides[0].crop.x + cropWidthSide;
              bodyprint.guides[0].crop.y2 =
                bodyprint.guides[0].crop.y + cropWidthSide;
              bodyprint.guides[0].crop.growthFactor =
                state.windowHeight /
                (bodyprint.guides[0].crop.height * state.windowHeight);
              bodyprint.guides[1].crop.x2 =
                bodyprint.guides[1].crop.x + cropWidthBack;
              bodyprint.guides[1].crop.y2 =
                bodyprint.guides[1].crop.y + cropWidthBack;
              bodyprint.guides[1].crop.growthFactor =
                state.windowHeight /
                (bodyprint.guides[1].crop.height * state.windowHeight);
            }
            commit("setBodyprint", bodyprint);
            commit("setLoading", false);
            resolve();
            return;
          }
          bodyprint.points.forEach(function(part) {
            pointsProcessed++;
            part.x =
              (part.x /
                (getters.getPictureWidth * growthFactorBack * cropWidthSide)) *
              (state.windowHeight / 1000);
            part.z =
              (part.z /
                (getters.getPictureWidth * growthFactorSide * cropWidthBack)) *
              (state.windowHeight / 1000);
            part.y = part.y / 1000;

            if (bodyprint.points.length === pointsProcessed) {
              commit("setBodyprint", bodyprint);
              resolve();
              setTimeout(() => {
                commit("setLoading", false);
              }, 1000);
            }
            if (cropWidthSide) {
              bodyprint.guides[0].crop.x2 =
                bodyprint.guides[0].crop.x + cropWidthSide;
              bodyprint.guides[0].crop.y2 =
                bodyprint.guides[0].crop.y + cropWidthSide;
              bodyprint.guides[0].crop.growthFactor =
                state.windowHeight /
                (bodyprint.guides[0].crop.height * state.windowHeight);
              bodyprint.guides[1].crop.x2 =
                bodyprint.guides[1].crop.x + cropWidthBack;
              bodyprint.guides[1].crop.y2 =
                bodyprint.guides[1].crop.y + cropWidthBack;
              bodyprint.guides[1].crop.growthFactor =
                state.windowHeight /
                (bodyprint.guides[1].crop.height * state.windowHeight);
            }
          });
        }, 1000);
      });
    },
    postBodyprint(
      { commit, state, getters, rootGetters },
      { onlyModified = false } = {}
    ) {
      const patchChanges = bodyprintCopy => {
        let id = state.bodyprint.id;
        if (!id) {
          id = state.bodyprint._id;
        }
        let user = state.bodyprint.user;
        let payload = bodyprintCopy;

        delete payload.blur_status;
        delete payload.status;
        delete payload.user;
        delete payload.id;
        delete payload._id;
        delete payload.last_updated_at;
        delete payload.created_at;
        delete payload.status_retailer;
        delete payload.completed_at;
        delete payload.archived;
        delete payload.archived_at;

        rootGetters["network/getClient"]
          .patch(`user/${user}/bodyprint/${id}`, payload)
          .then(response => {
            // Update the status of the bodyprint in state with patch data
            var patchBodyprint = response.data;
            var stateBodyprint = state.bodyprint;
            stateBodyprint.status = patchBodyprint.status;
            stateBodyprint.blur_status = patchBodyprint.blur_status;
            commit("setBodyprint", stateBodyprint);
          });
      };

      var imgBack = new Image();
      var imgSide = new Image();

      imgBack.src = state.imageBack;
      imgSide.src = state.imageSide;

      let growthFactorSide =
        imgSide.height /
        (state.bodyprint.guides.find(x => x.projection === "left").crop.height *
          imgSide.height);

      let growthFactorBack =
        imgBack.height /
        (state.bodyprint.guides.find(x => x.projection === "back").crop.height *
          imgBack.height);

      let cropWidthSide = state.bodyprint.guides.find(
        x => x.projection === "left"
      ).crop.width;
      let cropWidthBack = state.bodyprint.guides.find(
        x => x.projection === "back"
      ).crop.width;

      let bodyprint = JSON.parse(JSON.stringify(state.bodyprint));
      let bodyprintCopy = { ...bodyprint };

      if (onlyModified) {
        bodyprintCopy.points = bodyprintCopy.points.filter(point =>
          state.modifiedPoints.includes(point.name)
        );
      }

      var pointsProcessed = 0;

      if (bodyprintCopy.points.length === 0) {
        patchChanges(bodyprintCopy);
      }

      bodyprintCopy.points.forEach(function(part) {
        pointsProcessed++;
        part.x =
          (part.x *
            (getters.getPictureWidth * growthFactorBack * cropWidthBack)) /
          (state.windowHeight / 1000);
        part.z =
          (part.z *
            (getters.getPictureWidth * growthFactorSide * cropWidthSide)) /
          (state.windowHeight / 1000);
        part.y = part.y * 1000;

        if (bodyprintCopy.points.length === pointsProcessed) {
          patchChanges(bodyprintCopy);
        }
      });
    },
    checkQueue({ commit, rootGetters }, id) {
      return rootGetters["network/getClient"]
        .get(`bodyprint_queue/check/${id}`)
        .then(response => {
          if (response.data.inQueue) {
            var queueItem = response.data.item;
            commit("setQueueItem", queueItem);
          }
        });
    },
    getFirstInQueue({ commit, rootGetters }) {
      return rootGetters["network/getClient"]
        .get(`bodyprint_queue/next`)
        .then(response => {
          var bodyprint = response.data.body_print;
          commit("setBodyprint", bodyprint);
          commit("setQueueItem", response.data);
        })
        .catch(error => {
          if (error.response) {
            if (error.response.data["MSG"]) {
              if (error.response.data["MSG"] === "QUEUE EMPTY") {
                commit("setQueueEmpty", true);
              }
            }
          }
        });
    },
    getBodyprintByUser({ commit, rootGetters }, payload) {
      return rootGetters["network/getClient"]
        .post(`bodyprint_queue/search-user`, {
          email: payload.email,
          code: payload.code
        })
        .then(response => {
          var bodyprint = response.data.body_print;
          commit("setBodyprint", bodyprint);
          commit("setQueueItem", response.data);
        });
    },
    getBodyprintsByUser({ commit, rootGetters }, payload) {
      return rootGetters["network/getClient"]
        .post(`bodyprint_queue/queue-search-user`, {
          email: payload.email,
          code: payload.code
        })
        .then(response => {
          var bodyprints = response.data;
          commit("setBodyprints", bodyprints);
        });
    },
    getBodyprintsForRetailer({ commit, rootGetters }, payload) {
      return rootGetters["network/getClient"]
        .post(`bodyprint_queue/queue-search-for-retailer`, payload)
        .then(response => {
          var bodyprints = response.data;
          commit("setBodyprints", bodyprints);
        });
    },
    getDeclinedBodyprints({ commit, rootGetters }) {
      return rootGetters["network/getClient"]
        .post(`bodyprint_queue/declined`, {})
        .then(response => {
          var bodyprints = response.data;
          commit("setDeclinedBodyprints", bodyprints);
        });
    },
    restoreDeclinedBodyprint({ rootGetters }, payload) {
      return rootGetters["network/getClient"].post(
        `bodyprint_queue/${payload.id}/restore`,
        {}
      );
    },
    transferDeclinedBodyprint({ rootGetters }, payload) {
      return rootGetters["network/getClient"].post(
        `bodyprint_queue/${payload.id}/transfer`,
        payload.data
      );
    },
    removeDeclinedBodyprint({ rootGetters }, payload) {
      return rootGetters["network/getClient"].post(
        `bodyprint_queue/${payload.id}/remove`,
        {}
      );
    },
    requestBodyprint({ commit, rootGetters }, payload) {
      return rootGetters["network/getClient"]
        .post(`bodyprint_queue/request-bodyprint`, {
          queue_id: payload.id
        })
        .then(response => {
          var bodyprint = response.data.body_print;
          commit("setBodyprint", bodyprint);
          commit("setQueueItem", response.data);
        });
    },
    requestBodyprintToView({ commit, rootGetters }, payload) {
      return rootGetters["network/getClient"]
        .post(`bodyprint_queue/request-bodyprint-to-view`, {
          queue_id: payload.id
        })
        .then(response => {
          var bodyprint = response.data.body_print;
          commit("setBodyprint", bodyprint);
          commit("setQueueItem", response.data);
        });
    },
    requestBodyprintToBlur({ commit, rootGetters }, payload) {
      return rootGetters["network/getClient"]
        .post(`bodyprint_queue/request-bodyprint-to-blur`, {
          queue_id: payload.id
        })
        .then(response => {
          var bodyprint = response.data.body_print;
          commit("setBodyprint", bodyprint);
          commit("setQueueItem", response.data);
        });
    },
    requestBodyprintInfo({ commit, rootGetters }, payload) {
      return rootGetters["network/getClient"]
        .post(`bodyprint_queue/request-bodyprint-info`, {
          queue_id: payload.id
        })
        .then(response => {
          commit("setBodyprintInfo", response.data);
        });
    },
    requestBodyprintSizes({ commit, rootGetters }, payload) {
      return rootGetters["network/getClient"]
        .post(`bodyprint_queue/request-bodyprint-sizes`, {
          queue_id: payload.id
        })
        .then(response => {
          commit("setBodyprintSizes", response.data);
        });
    },
    getBodyprintByUserRaw({ commit, rootGetters }, payload) {
      return rootGetters["network/getClient"]
        .post(`bodyprint_queue/search-user-sizes`, {
          email: payload.email,
          code: payload.code
        })
        .then(response => {
          commit("setBodyprintSizes", response.data);
        });
    },
    getBodyprint({ dispatch, rootGetters }, params) {
      return rootGetters["network/getClient"]
        .get(`user/${params.id}/bodyprint`)
        .then(response => {
          var bodyprint;
          if (params.bodyprint_id) {
            bodyprint = response.data.find(x => x.id === params.bodyprint_id);
          } else {
            bodyprint = response.data[response.data.length - 1];
          }
          return dispatch("setBodyprint", bodyprint);
        });
    },
    getImages({ commit, rootGetters }, id) {
      return rootGetters["network/getClient"]
        .get(`admin-user/${id}/images`)
        .then(response => commit("setImages", response.data));
    },
    getBlurredImages({ commit, rootGetters }, id) {
      return rootGetters["network/getClient"]
        .get(`admin-user/${id}/blurred_images`)
        .then(response => commit("setImages", response.data));
    },
    updateBlurData({ dispatch, rootGetters }, { imageId, faces, userId }) {
      return rootGetters["network/getClient"]
        .post(`admin-user/image/${imageId}/edit_blur`, { faces })
        .then(() => {
          return dispatch("getBlurredImages", userId);
        });
    },
    getBodyprintSizes({ commit, rootGetters }, params) {
      return rootGetters["network/getClient"]
        .get(`user/${params.user}/measurement/${params.bodyprint}`)
        .then(response => {
          commit("setBodyprintSizes", response.data);
        })
        .catch(error => commit("setBodyprintSizes", error.response.data));
    },
    getBodyprintMeasurements({ commit, state, getters, rootGetters }) {
      const getMeasurements = bp => {
        let payload = bp;

        delete payload.guides[0].crop.x2;
        delete payload.guides[0].crop.y2;
        delete payload.guides[0].crop.growthFactor;
        delete payload.guides[1].crop.x2;
        delete payload.guides[1].crop.y2;
        delete payload.guides[1].crop.growthFactor;

        rootGetters["network/getClient"]
          .post(`measurement/bodyprint_data`, bp)
          .then(response => {
            commit("setBodyprintSizes", response.data);
          });
      };

      var imgBack = new Image();
      var imgSide = new Image();

      imgBack.src = state.imageBack;
      imgSide.src = state.imageSide;

      let growthFactorSide =
        imgSide.height /
        (state.bodyprint.guides.find(x => x.projection === "left").crop.height *
          imgSide.height);

      let growthFactorBack =
        imgBack.height /
        (state.bodyprint.guides.find(x => x.projection === "back").crop.height *
          imgBack.height);

      let cropWidthSide = state.bodyprint.guides.find(
        x => x.projection === "left"
      ).crop.width;
      let cropWidthBack = state.bodyprint.guides.find(
        x => x.projection === "back"
      ).crop.width;

      let bodyprint = JSON.parse(JSON.stringify(state.bodyprint));

      var pointsProcessed = 0;

      bodyprint.points.forEach(function(part) {
        pointsProcessed++;
        part.x =
          (part.x *
            (getters.getPictureWidth * growthFactorBack * cropWidthBack)) /
          (state.windowHeight / 1000);
        part.z =
          (part.z *
            (getters.getPictureWidth * growthFactorSide * cropWidthSide)) /
          (state.windowHeight / 1000);
        part.y = part.y * 1000;

        if (bodyprint.points.length === pointsProcessed) {
          getMeasurements(bodyprint);
        }
      });
    },
    solveConstraint({ commit, rootGetters }, constraint) {
      const payload = rootGetters["configuration/getPoint"](
        constraint.puppetPointName
      );
      let referenceValues = {
        x: 0,
        y: 0,
        z: 0
      };

      if (constraint.referencePointName) {
        const referencePoint = rootGetters["configuration/getPoint"](
          constraint.referencePointName
        );
        referenceValues = { ...referencePoint };
      }
      const axises = [];
      for (const axis in constraint.axis) {
        if (constraint.axis[axis]) {
          axises.push(axis);
        }
      }

      for (const index in axises) {
        const currentAxis = axises[index];
        if (constraint.relation === "<=") {
          if (
            payload[currentAxis] >
            referenceValues[currentAxis] + constraint.distance
          ) {
            payload[currentAxis] =
              referenceValues[currentAxis] + constraint.distance;
          }
        }
        if (constraint.relation === ">=") {
          if (
            payload[currentAxis] <
            referenceValues[currentAxis] + constraint.distance
          ) {
            payload[currentAxis] =
              referenceValues[currentAxis] + constraint.distance;
          }
        }
        if (constraint.relation === "=") {
          if (
            payload[currentAxis] !==
            referenceValues[currentAxis] + constraint.distance
          ) {
            payload[currentAxis] =
              referenceValues[currentAxis] + constraint.distance;
          }
        }
        if (constraint.relation === "avg") {
          const avgPoint = rootGetters["configuration/getPoint"](
            constraint.averageReferencePointName
          );
          payload[currentAxis] =
            (avgPoint[currentAxis] + referenceValues[currentAxis]) / 2;
        }
      }
      commit("setPoint", payload);
    },
    setModifiedPoint({ commit }, name) {
      commit("setModifiedPoint", name);
    },
    recalculatePoints({ dispatch, rootState }) {
      const runConstraints = () => {
        for (const constraintIndex in rootState.configuration.constraints) {
          dispatch(
            "solveConstraint",
            rootState.configuration.constraints[constraintIndex]
          );
        }
      };
      const runIntersections = () => {
        rootState.configuration.intersections.forEach(intersection => {
          dispatch("solveIntersections", intersection);
        });
      };
      return Promise.resolve()
        .then(() => {
          runConstraints();
          runIntersections();
        })
        .then(() => {
          // Second pass so constraints that reference intersection results pick up the updated values.
          runConstraints();
          runIntersections();
        });
    },
    setPoint({ dispatch, commit, rootState }, point) {
      commit("setPoint", point);
      commit("setModifiedPoint", point.name);
      const constraints = rootState.configuration.constraints.filter(
        constraint => constraint.puppetPointName === point.name
      );

      for (let constraintIndex in constraints) {
        const constraint = constraints[constraintIndex];
        dispatch("solveConstraint", constraint);
      }

      for (let constraintIndex in rootState.configuration.constraints) {
        const constraint = rootState.configuration.constraints[constraintIndex];
        if (constraint.puppetPointName !== point.name) {
          dispatch("solveConstraint", constraint);
        }
      }

      // Update intersections
      rootState.configuration.intersections.forEach(intersection => {
        var line = intersection.line;
        var planarLineA = intersection.planarLineA;
        var planarLineB = intersection.planarLineB;

        if (
          point.name === line.startPointName ||
          point.name === line.endPointName ||
          point.name === planarLineA.startPointName ||
          point.name === planarLineA.endPointName ||
          point.name === planarLineB.startPointName ||
          point.name === planarLineB.endPointName
        ) {
          dispatch("solveIntersections", intersection);
        }
      });
      rootState.configuration.intersections.forEach(intersection => {
        var line = intersection.line;
        var planarLineA = intersection.planarLineA;
        var planarLineB = intersection.planarLineB;

        if (
          point.name !== line.startPointName &&
          point.name !== line.endPointName &&
          point.name !== planarLineA.startPointName &&
          point.name !== planarLineA.endPointName &&
          point.name !== planarLineB.startPointName &&
          point.name !== planarLineB.endPointName
        ) {
          dispatch("solveIntersections", intersection);
        }
      });
    },
    solveIntersections({ commit, rootGetters }, intersection) {
      var puppetPoint = rootGetters["configuration/getPoint"](
        intersection.puppetPointName
      );
      var newPoint;

      var lineStartPoint = rootGetters["configuration/getPoint"](
        intersection.line.startPointName
      );
      var lineEndPoint = rootGetters["configuration/getPoint"](
        intersection.line.endPointName
      );

      var planarLineAStartPoint = rootGetters["configuration/getPoint"](
        intersection.planarLineA.startPointName
      );
      var planarLineAEndPoint = rootGetters["configuration/getPoint"](
        intersection.planarLineA.endPointName
      );
      var planarLineBStartPoint = rootGetters["configuration/getPoint"](
        intersection.planarLineB.startPointName
      );
      var planarLineBEndPoint = rootGetters["configuration/getPoint"](
        intersection.planarLineB.endPointName
      );

      var planarVectorA = new VectorToFrom(
        planarLineAStartPoint,
        planarLineAEndPoint
      );
      var planarVectorB = new VectorToFrom(
        planarLineBStartPoint,
        planarLineBEndPoint
      );

      var plane = new Plane(
        planarLineAStartPoint,
        planarVectorA,
        planarVectorB
      );

      var intersectionPoint = plane.planeIntersectPoints(
        lineStartPoint,
        lineEndPoint
      );
      intersectionPoint.name = puppetPoint.name;
      newPoint = intersectionPoint;

      commit("setPoint", newPoint);
    },
    // List v2
    getAllUsersInQueue({ commit, rootGetters }, payload) {
      return rootGetters["network/getClient"]
        .post(`bodyprint_queue/measure/queue-user-list`, {
          email: payload.email,
          code: payload.code,
          retailer: payload.retailer,
          status: payload.status
        })
        .then(response => {
          var users = response.data;
          commit("setUsersInQueue", users);
        });
    },
    getBodyprintQueueForUser({ commit, rootGetters }, payload) {
      return rootGetters["network/getClient"]
        .post(`bodyprint_queue/measure/get-queue-for-user`, {
          email: payload.email,
          code: payload.code,
          retailer: payload.retailer,
          status: payload.status
        })
        .then(response => {
          commit("setItemsForUser", response.data);
        });
    },
    getMeasurementsForQueue({ commit, rootGetters }, payload) {
      return rootGetters["network/getClient"]
        .post(`bodyprint_queue/measure/get-measurements-for-queue`, {
          email: payload.email,
          code: payload.code,
          status: payload.status
        })
        .then(response => commit("setItemsForQueueItem", response.data));
    },
    // Blur queue
    getAllUsersInBlurQueue({ commit, rootGetters }, payload) {
      return rootGetters["network/getClient"]
        .post(`bodyprint_queue/blur/queue-user-list`, {
          email: payload.email,
          code: payload.code,
          retailer: payload.retailer,
          blurStatus: payload.blurStatus
        })
        .then(response => {
          commit("setUsersInBlurQueue", response.data);
        });
    },
    getBlurQueueForUser({ commit, rootGetters }, payload) {
      return rootGetters["network/getClient"]
        .post(`bodyprint_queue/blur/get-queue-for-user`, {
          email: payload.email,
          code: payload.code,
          retailer: payload.retailer,
          blurStatus: payload.blurStatus
        })
        .then(response => {
          commit("setItemsForBlurUser", response.data);
        });
    },
    updateBlurStatusAuto({ rootGetters }, queueId) {
      return rootGetters["network/getClient"].post(
        `bodyprint_queue/${queueId}/blur-status/auto`
      );
    },
    updateBlurStatus(
      { rootGetters },
      { queueId, blurStatus, updateOriginalBodyprint }
    ) {
      return rootGetters["network/getClient"].post(
        `bodyprint_queue/${queueId}/blur-status`,
        {
          blur_status: blurStatus,
          update_original_bodyprint: updateOriginalBodyprint
        }
      );
    },
    fetchBlurResizeMarginSetting({ commit, rootGetters }) {
      return rootGetters["network/getClient"]
        .get(`settings/blur_resize_margin_percentage`)
        .then(response => {
          const value = parseFloat(response.data.value);
          if (!isNaN(value)) {
            commit("setBlurSimpleResizeMargin", value);
          }
        });
    }
  }
};
